import { Button, Card, ChoiceList, EmptySearchResult, IndexFilters, IndexTable, Layout, useBreakpoints, Spinner, useSetIndexFiltersMode, Text, TextField, Box, InlineStack } from '@shopify/polaris';
import { ViewIcon } from '@shopify/polaris-icons';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import StoreLocation from '#client/components/StoreLocation';
import ExportButton from '#client/components/ExportButton/ExportButton';

import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { formatDateTime } from '#client/utils/helpers';
import { inventoryTransferService } from '#client/services/inventoryTransferServices';

export default function TransferIndexTable(props) {
    const { tableHeadings, panelTitle, locationsAccess, incomingLocation, outgoingLocation, scannedValue } = props;
    const navigate = useNavigate();
    const hasMounted = useRef(false);
    const { currentUser } = useContext(SessionContext);
    const breakpoints = useBreakpoints();

    let transferFilterSessionName = 'transferFilters_';
    if (locationsAccess) transferFilterSessionName += 'global';
    if (incomingLocation) transferFilterSessionName += `incoming_${incomingLocation.substring(incomingLocation.lastIndexOf('/') + 1)}`;
    if (outgoingLocation) transferFilterSessionName += `outgoing_${outgoingLocation.substring(outgoingLocation.lastIndexOf('/') + 1)}`;

    const [locationFilterOptions, setLocationFilterOptions] = useState([]);
    const [savedFilters, setSavedFilters] = useState(null); // savedFiltersRef.current;
    useEffect(() => {
        if (!hasMounted.current) {
            setQueryValue('');
        }
    }, [scannedValue]);

    const parseSortValue = (sortValue) => {
        const sortMap = {
            ['displayTransferId asc']: { index: 0, direction: 'asc' },
            ['displayTransferId']: { index: 0, direction: 'desc' },
            ['status asc']: { index: 1, direction: 'asc' },
            ['status desc']: { index: 1, direction: 'desc' },
            ['type asc']: { index: 2, direction: 'asc' },
            ['type desc']: { index: 2, direction: 'desc' },
            ['fromLocation asc']: { index: 3, direction: 'asc' },
            ['fromLocation desc']: { index: 3, direction: 'desc' },
            ['toLocation asc']: { index: 4, direction: 'asc' },
            ['toLocation desc']: { index: 4, direction: 'desc' },
            ['qtyShipped asc']: { index: 5, direction: 'asc' },
            ['qtyShipped desc']: { index: 5, direction: 'desc' },
            ['qtyReceived asc']: { index: 6, direction: 'asc' },
            ['qtyReceived desc']: { index: 6, direction: 'desc' },
            ['createdDate asc']: { index: 7, direction: 'asc' },
            ['createdDate desc']: { index: 7, direction: 'desc' },
            ['updatedDate asc']: { index: 8, direction: 'asc' },
            ['updatedDate desc']: { index: 8, direction: 'desc' },
            ['shippedDate asc']: { index: 9, direction: 'asc' },
            ['shippedDate desc']: { index: 9, direction: 'desc' },
        };
        return sortMap[sortValue] || { index: 8, direction: 'desc' };
    };

    const isEmpty = (value) => {
        if (Array.isArray(value)) {
            return value.length === 0;
        } else {
            return value === '' || value == null;
        }
    };

    const isLoadingOnStoreChange = useRef(false);
    const loadFiltersFromSession = () => {
        try {
            const raw = sessionStorage.getItem(transferFilterSessionName);
            const filters = raw ? JSON.parse(raw) : null;

            if (!filters) return;

            setSavedFilters(filters);
            if (filters) {
                setQueryValue(filters.queryValue);
                setStatusFilter(filters.statusFilter);
                setTypeFilter(filters.typeFilter);
                setCreatedDateStartFilter(createdDateStartFilter);
                setCreatedDateEndFilter(createdDateEndFilter);
                setUpdatedDateStartFilter(updatedDateStartFilter);
                setUpdatedDateEndFilter(updatedDateEndFilter);
                setShippedDateStartFilter(shippedDateStartFilter);
                setShippedDateEndFilter(shippedDateEndFilter);
                setFromLocationFilter(filters.fromLocationFilter);
                setToLocationFilter(filters.toLocationFilter);
            } else {
                setQueryValue('');
                setStatusFilter([]);
                setTypeFilter('');
                setCreatedDateStartFilter('');
                setCreatedDateEndFilter('');
                setUpdatedDateStartFilter('');
                setUpdatedDateEndFilter('');
                setShippedDateStartFilter('');
                setShippedDateEndFilter('');
                setFromLocationFilter([]);
                setToLocationFilter([]);
            }
        } catch (err) {
            console.warn('Failed to parse filters from session:', err);
            return null;
        }
    };

    const saveFiltersToSession = (filters) => {
        try {
            if (!isLoadingOnStoreChange.current) {
                sessionStorage.setItem(transferFilterSessionName, JSON.stringify(filters));
            }
            isLoadingOnStoreChange.current = false;
        } catch (err) {
            console.error('Error saving filters to sessionStorage:', err);
        }
    };

    const getActiveStoreFilter = () => {
        const activeStore = currentUser?.activeStore;
        if (Array.isArray(activeStore)) {
            return activeStore.filter((storeId) => storeId);
        }
        return activeStore ? [activeStore] : [];
    };

    const fetchTransferData = async (page) => {
        setDataState((prev) => ({ ...prev, isLoading: true }));

        try {
            const filters = { ...currentFilters };

            //Add sorting parameters
            if (sortSelected.length > 0) {
                const { index, direction } = parseSortValue(sortSelected[0]);
                filters.sortColumn = index;
                filters.sortDirection = direction;
            }

            const result = await inventoryTransferService.fetchPaginatedTransfers(page, itemsPerPage, filters);

            setDataState({
                rows: result.rows,
                total: result.total,
                isLoading: false,
            });
        } catch (error) {
            setDataState({
                rows: [],
                total: 0,
                isLoading: false,
            });
        }
    };

    //const savedFiltersRef = useRef(loadFiltersFromSession()); // stable copy
    //    const { savedFilters, setSavedFilters } = useState(null); // savedFiltersRef.current;

    const [queryValue, setQueryValue] = useState(savedFilters?.queryValue || '');
    const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);
    const handleSearchFieldCancelButtonClick = () => {
        setQueryValue('');
    };
    const handleQueryFilterChange = useCallback((value) => {
        setQueryValue(value);
    }, []);

    const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || '');
    const handleStatusFilterRemove = useCallback(() => setStatusFilter(''), []);
    const handleStatusFilterChange = useCallback((value) => {
        setStatusFilter(value);
    }, []);

    const [typeFilter, setTypeFilter] = useState(savedFilters?.typeFilter || '');
    const handleTypeFilterRemove = useCallback(() => setTypeFilter(''), []);
    const handleTypeFilterChange = useCallback((value) => {
        setTypeFilter(value);
    }, []);

    const [globalLocationFilter, setGlobalLocationFilter] = useState(savedFilters?.globalLocationFilter || []);
    const handleGlobalLocationFilterRemove = useCallback(() => setGlobalLocationFilter(locationsAccess));

    const [fromLocationFilter, setFromLocationFilter] = useState(savedFilters?.fromLocationFilter || []);
    const handleFromLocationFilterRemove = useCallback(() => {
        setFromLocationFilter([]);
    }, []);

    const handleFromLocationFilterChange = useCallback((value) => {
        setFromLocationFilter(value);
    }, []);

    const [toLocationFilter, setToLocationFilter] = useState(savedFilters?.toLocationFilter || []);
    const handleToLocationFilterRemove = useCallback(() => {
        setToLocationFilter([]);
    }, []);
    const handleToLocationFilterChange = useCallback((value) => {
        setToLocationFilter(value);
    }, []);

    const handleCreatedDateRangeRemove = useCallback(() => {
        setCreatedDateStartFilter('');
        setCreatedDateEndFilter('');
    });

    const handleUpdatedDateRangeRemove = useCallback(() => {
        setUpdatedDateStartFilter('');
        setUpdatedDateEndFilter('');
    });

    const handleShippedDateRangeRemove = useCallback(() => {
        setShippedDateStartFilter('');
        setShippedDateEndFilter('');
    });

    const [createdDateStartFilter, setCreatedDateStartFilter] = useState(savedFilters?.createdDateStartFilter || '');
    const handleCreatedDateStartFilterChange = useCallback((value) => {
        setCreatedDateStartFilter(value);
    }, []);

    const [createdDateEndFilter, setCreatedDateEndFilter] = useState(savedFilters?.createdDateEndFilter || '');
    const handleCreatedDateEndFilterChange = useCallback((value) => {
        setCreatedDateEndFilter(value);
    }, []);

    const [updatedDateStartFilter, setUpdatedDateStartFilter] = useState(savedFilters?.updatedDateStartFilter || '');
    const handleUpdatedDateStartFilterChange = useCallback((value) => {
        setUpdatedDateStartFilter(value);
    }, []);

    const [updatedDateEndFilter, setUpdatedDateEndFilter] = useState(savedFilters?.updatedDateEndFilter || '');
    const handleUpdatedDateEndFilterChange = useCallback((value) => {
        setUpdatedDateEndFilter(value);
    }, []);

    const [shippedDateStartFilter, setShippedDateStartFilter] = useState(savedFilters?.shippedDateStartFilter || '');
    const handleShippedDateStartFilterChange = useCallback((value) => {
        setShippedDateStartFilter(value);
    }, []);

    const [shippedDateEndFilter, setShippedDateEndFilter] = useState(savedFilters?.shippedDateEndFilter || '');
    const handleShippedDateEndFilterChange = useCallback((value) => {
        setShippedDateEndFilter(value);
    }, []);

    const getScopedLocationFilters = () => {
        const activeStoreFilter = getActiveStoreFilter();

        if (incomingLocation) {
            return {
                toLocationFilter: activeStoreFilter,
                fromLocationFilter,
            };
        }

        if (outgoingLocation) {
            return {
                fromLocationFilter: activeStoreFilter,
                toLocationFilter,
            };
        }

        return {
            fromLocationFilter,
            toLocationFilter,
        };
    };

    const handleFiltersClearAll = useCallback(() => {
        handleQueryValueRemove();
        handleStatusFilterRemove();
        handleTypeFilterRemove();
        handleGlobalLocationFilterRemove();
        handleToLocationFilterRemove();
        handleFromLocationFilterRemove();
        handleCreatedDateRangeRemove();
        handleUpdatedDateRangeRemove();
        handleShippedDateRangeRemove();

        setSavedFilters(null);
        if (incomingLocation) {
            setFromLocationFilter([]);
            setToLocationFilter(getActiveStoreFilter());
        } else if (outgoingLocation) {
            setFromLocationFilter(getActiveStoreFilter());
            setToLocationFilter([]);
        } else {
            setFromLocationFilter([]);
            setToLocationFilter([]);
        }

        // Also clear session storage when clearing all filters
        sessionStorage.removeItem(transferFilterSessionName);
    }, []);

    const statusFilterOptions = [
        { label: 'Draft', value: 'Draft' },
        { label: 'Initiated', value: 'Initiated' },
        { label: 'Canceled', value: 'Canceled' },
        { label: 'In Transit', value: 'In Transit' },
        { label: 'Draft Receiving', value: 'Draft Receiving' },
        { label: 'Exception', value: 'Exception' },
        { label: 'Complete', value: 'Complete' },
    ];

    const typeFilterOptions = [
        { label: 'Manual', value: 'Manual' },
        { label: 'Automatic', value: 'Automatic' },
    ];

    const filterOptions = [
        {
            key: 'status',
            label: 'Status',
            filter: (
                <ChoiceList title="Status" titleHidden choices={statusFilterOptions} selected={Array.isArray(statusFilter) ? statusFilter : []} onChange={handleStatusFilterChange} allowMultiple />
            ),
            shortcut: true,
        },
        {
            key: 'type',
            label: 'Type',
            filter: <ChoiceList title="Type" titleHidden choices={typeFilterOptions} selected={Array.isArray(typeFilter) ? typeFilter : []} onChange={handleTypeFilterChange} />,
            shortcut: true,
        },
        ...(isEmpty(outgoingLocation)
            ? [
                  {
                      key: 'fromLocation',
                      label: 'From Location',
                      filter: <ChoiceList title="Type" titleHidden choices={locationFilterOptions} selected={fromLocationFilter} onChange={handleFromLocationFilterChange} />,
                      shortcut: true,
                  },
              ]
            : []),
        ...(isEmpty(incomingLocation)
            ? [
                  {
                      key: 'toLocation',
                      label: 'To Location',
                      filter: <ChoiceList title="Type" titleHidden choices={locationFilterOptions} selected={toLocationFilter} onChange={handleToLocationFilterChange} />,
                      shortcut: true,
                  },
              ]
            : []),
        {
            key: 'createdDate',
            label: 'Date Created',
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={createdDateStartFilter} onChange={handleCreatedDateStartFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">to</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={createdDateEndFilter} onChange={handleCreatedDateEndFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: 'updatedDate',
            label: 'Last Updated',
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={updatedDateStartFilter} onChange={handleUpdatedDateStartFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">to</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={updatedDateEndFilter} onChange={handleUpdatedDateEndFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: 'shippedDate',
            label: 'Date Shipped',
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={shippedDateStartFilter} onChange={handleShippedDateStartFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">to</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={shippedDateEndFilter} onChange={handleShippedDateEndFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
    ];

    const disambiguateLabel = (key, value) => {
        switch (key) {
            case 'status':
                return `Status: ${value.join(', ')}`;
            case 'type':
                return `Type: ${value.join(', ')}`;
            case 'fromLocation':
                return `From Location: ${
                    currentUser.locationMappedNames.find((location) => {
                        return location.id == value;
                    }).name
                }`;
            case 'toLocation':
                return `To Location: ${
                    currentUser.locationMappedNames.find((location) => {
                        return location.id == value;
                    }).name
                }`;
            default:
                return value;
        }
    };
    const isScopedToActiveStore = (value) => {
        const activeStoreFilter = getActiveStoreFilter();
        return Array.isArray(value) && value.length === 1 && activeStoreFilter.length === 1 && value[0] === activeStoreFilter[0];
    };

    const appliedFilters = [];

    if (statusFilter && !isEmpty(statusFilter)) {
        appliedFilters.push({
            key: 'status',
            label: disambiguateLabel('status', statusFilter),
            onRemove: handleStatusFilterRemove,
        });
    }

    if (typeFilter && !isEmpty(typeFilter)) {
        appliedFilters.push({
            key: 'type',
            label: disambiguateLabel('type', typeFilter),
            onRemove: handleTypeFilterRemove,
        });
    }

    if (fromLocationFilter && !isEmpty(fromLocationFilter) && !isScopedToActiveStore(fromLocationFilter)) {
        appliedFilters.push({
            key: 'fromLocation',
            label: disambiguateLabel('fromLocation', fromLocationFilter),
            onRemove: handleFromLocationFilterRemove,
        });
    }

    if (toLocationFilter && !isEmpty(toLocationFilter) && !isScopedToActiveStore(toLocationFilter)) {
        appliedFilters.push({
            key: 'toLocation',
            label: disambiguateLabel('toLocation', toLocationFilter),
            onRemove: handleToLocationFilterRemove,
        });
    }

    if ((createdDateStartFilter && !isEmpty(createdDateStartFilter)) || (createdDateEndFilter && !isEmpty(createdDateEndFilter))) {
        const dateRangeLabel = [];
        if (createdDateStartFilter && createdDateEndFilter) {
            dateRangeLabel.push(`${createdDateStartFilter} to ${createdDateEndFilter}`);
        } else if (createdDateStartFilter) {
            dateRangeLabel.push(`From ${createdDateStartFilter}`);
        } else if (createdDateEndFilter) {
            dateRangeLabel.push(`To ${createdDateEndFilter}`);
        }

        appliedFilters.push({
            key: 'createdDate',
            label: `Date Created: ${dateRangeLabel.join('')}`,
            onRemove: handleCreatedDateRangeRemove,
        });
    }

    if ((updatedDateStartFilter && !isEmpty(updatedDateStartFilter)) || (updatedDateEndFilter && !isEmpty(updatedDateEndFilter))) {
        const dateRangeLabel = [];
        if (updatedDateStartFilter && updatedDateEndFilter) {
            dateRangeLabel.push(`${updatedDateStartFilter} to ${updatedDateEndFilter}`);
        } else if (updatedDateStartFilter) {
            dateRangeLabel.push(`From ${updatedDateStartFilter}`);
        } else if (updatedDateEndFilter) {
            dateRangeLabel.push(`To ${updatedDateEndFilter}`);
        }

        appliedFilters.push({
            key: 'updatedDate',
            label: `Last Updated: ${dateRangeLabel.join('')}`,
            onRemove: handleUpdatedDateRangeRemove,
        });
    }

    if ((shippedDateStartFilter && !isEmpty(shippedDateStartFilter)) || (shippedDateEndFilter && !isEmpty(shippedDateEndFilter))) {
        const dateRangeLabel = [];
        if (shippedDateStartFilter && shippedDateEndFilter) {
            dateRangeLabel.push(`${shippedDateStartFilter} to ${shippedDateEndFilter}`);
        } else if (shippedDateStartFilter) {
            dateRangeLabel.push(`From ${shippedDateStartFilter}`);
        } else if (shippedDateEndFilter) {
            dateRangeLabel.push(`To ${shippedDateEndFilter}`);
        }

        appliedFilters.push({
            key: 'shippedDate',
            label: `Date Shipped: ${dateRangeLabel.join('')}`,
            onRemove: handleShippedDateRangeRemove,
        });
    }

    const sortOptions = [
        { label: 'Transfer ID', value: 'displayTransferId asc', directionLabel: 'Ascending' },
        { label: 'Transfer ID', value: 'displayTransferId desc', directionLabel: 'Descending' },
        { label: 'Status', value: 'status asc', directionLabel: 'A-Z' },
        { label: 'Status', value: 'status desc', directionLabel: 'Z-A' },
        { label: 'Type', value: 'type asc', directionLabel: 'A-Z' },
        { label: 'Type', value: 'type desc', directionLabel: 'Z-A' },
        ...(isEmpty(incomingLocation)
            ? [
                  { label: 'To Location', value: 'toLocation asc', directionLabel: 'Ascending' },
                  { label: 'To Location', value: 'toLocation desc', directionLabel: 'Descending' },
              ]
            : []),
        ...(isEmpty(outgoingLocation)
            ? [
                  { label: 'From Location', value: 'fromLocation asc', directionLabel: 'Ascending' },
                  { label: 'From Location', value: 'fromLocation desc', directionLabel: 'Descending' },
              ]
            : []),
        { label: 'Qty Shipped', value: 'qtyShipped asc', directionLabel: '0-9' },
        { label: 'Qty Shipped', value: 'qtyShipped desc', directionLabel: '9-0' },
        { label: 'Qty Received', value: 'qtyReceived asc', directionLabel: '0-9' },
        { label: 'Qty Received', value: 'qtyReceived desc', directionLabel: '9-0' },
        { label: 'Date Created', value: 'createdDate asc', directionLabel: 'Ascending' },
        { label: 'Date Created', value: 'createdDate desc', directionLabel: 'Descending' },
        { label: 'Last Updated', value: 'updatedDate asc', directionLabel: 'Ascending' },
        { label: 'Last Updated', value: 'updatedDate desc', directionLabel: 'Descending' },
        { label: 'Date Shipped', value: 'shippedDate asc', directionLabel: 'Ascending' },
        { label: 'Date Shipped', value: 'shippedDate desc', directionLabel: 'Descending' },
    ];

    const emptyStateMarkup = <EmptySearchResult title="No Transfers Found" description="Try adjusting your filters or search criteria." withIllustration />;

    const resourceName = {
        singular: 'Transfer',
        plural: 'Transfers',
    };
    const itemsPerPage = 10;
    const { mode, setMode } = useSetIndexFiltersMode('FILTERING');
    const tabs = [];

    const [currentPage, setCurrentPage] = useState(savedFilters?.currentPage || 1);
    useEffect(() => {
        if (savedFilters?.currentPage && savedFilters.currentPage !== currentPage) {
            setCurrentPage(savedFilters.currentPage);
        }
    }, [savedFilters]);

    const [dataState, setDataState] = useState({
        rows: [],
        total: 0,
        isLoading: true,
    });

    const [sortSelected, setSortSelected] = useState(() => {
        const savedSort = savedFilters?.sortSelected;
        const first = Array.isArray(savedSort) ? savedSort[0] : null;

        const isValid = first && sortOptions.some((opt) => opt.value === first);
        const fallback = ['updatedDate desc'];

        const initialSort = isValid ? savedSort : fallback;

        return initialSort;
    });

    const currentFilters = useMemo(
        () => ({
            queryValue,
            statusFilter,
            typeFilter,
            globalLocationFilter: locationsAccess ?? globalLocationFilter,
            ...getScopedLocationFilters(),
            createdDateStartFilter,
            createdDateEndFilter,
            updatedDateStartFilter,
            updatedDateEndFilter,
            shippedDateStartFilter,
            shippedDateEndFilter,
            sortSelected,
        }),
        [
            queryValue,
            statusFilter,
            typeFilter,
            globalLocationFilter,
            fromLocationFilter,
            toLocationFilter,
            createdDateStartFilter,
            createdDateEndFilter,
            updatedDateStartFilter,
            updatedDateEndFilter,
            shippedDateStartFilter,
            shippedDateEndFilter,
            sortSelected,
            currentUser?.activeStore,
            incomingLocation,
            outgoingLocation,
            locationsAccess,
        ]
    );

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return; // ⛔️ skip initial save to sessionStorage
        }
        const filters = {
            queryValue,
            statusFilter,
            typeFilter,
            globalLocationFilter,
            fromLocationFilter,
            toLocationFilter,
            createdDateStartFilter,
            createdDateEndFilter,
            updatedDateStartFilter,
            updatedDateEndFilter,
            shippedDateStartFilter,
            shippedDateEndFilter,
            sortSelected,
            currentPage,
        };
        saveFiltersToSession(filters);
    }, [
        queryValue,
        statusFilter,
        typeFilter,
        globalLocationFilter,
        fromLocationFilter,
        toLocationFilter,
        createdDateStartFilter,
        createdDateEndFilter,
        updatedDateStartFilter,
        updatedDateEndFilter,
        shippedDateStartFilter,
        shippedDateEndFilter,
        sortSelected,
        currentPage,
    ]);

    const prevActiveStoreRef = useRef(null);

    useEffect(() => {
        const currentActiveStore = localStorage.getItem('activeStore');

        if (currentActiveStore !== prevActiveStoreRef.current) {
            prevActiveStoreRef.current = currentActiveStore;
            isLoadingOnStoreChange.current = true;

            loadFiltersFromSession();

            if (!sessionStorage.getItem(transferFilterSessionName)) {
                if (incomingLocation) {
                    setToLocationFilter(getActiveStoreFilter());
                }
                if (outgoingLocation) {
                    setFromLocationFilter(getActiveStoreFilter());
                }
            } else if (outgoingLocation) {
                setFromLocationFilter(getActiveStoreFilter());
                setToLocationFilter([]);
            } else {
                setFromLocationFilter([]);
                setToLocationFilter([]);
            }

            if (locationsAccess) {
                setGlobalLocationFilter(locationsAccess);
            }

            loadFiltersFromSession();
            setCurrentPage(1);
        }

        const filterOptions = currentUser?.locationMappedNames.map((location) => ({
            label: location.name,
            value: location.id,
        }));

        setLocationFilterOptions(filterOptions);
        fetchTransferData(currentPage);
    }, [currentUser]);

    // Reset to page 1 anytime filters OR search change
    useEffect(() => {
        // Don’t reset on first mount
        if (!hasMounted.current) return;

        setCurrentPage(1);
    }, [
        queryValue,
        statusFilter,
        typeFilter,
        fromLocationFilter,
        toLocationFilter,
        createdDateStartFilter,
        createdDateEndFilter,
        updatedDateStartFilter,
        updatedDateEndFilter,
        shippedDateStartFilter,
        shippedDateEndFilter,
    ]);

    useEffect(() => {
        if (locationFilterOptions.length > 0 && savedFilters) {
            if (savedFilters.fromLocationFilter) {
                setFromLocationFilter(savedFilters.fromLocationFilter);
            }
            if (savedFilters.toLocationFilter) {
                setToLocationFilter(savedFilters.toLocationFilter);
            }
        }
    }, [locationFilterOptions, savedFilters]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchTransferData(currentPage);
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [currentPage, currentFilters]);

    const calculatePagination = () => {
        const totalPages = Math.ceil(dataState.total / itemsPerPage);
        const hasNext = currentPage < totalPages;
        const hasPrevious = currentPage > 1;

        return {
            hasNext,
            hasPrevious,
            totalPages,
        };
    };

    const handleNextPage = useCallback(() => {
        setCurrentPage((prevPage) => prevPage + 1);
    }, []);

    const handlePreviousPage = useCallback(() => {
        setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
    }, []);

    const { hasNext, hasPrevious } = calculatePagination();
    const startRow = (currentPage - 1) * itemsPerPage + 1;
    const endRow = Math.min(currentPage * itemsPerPage, dataState.total);
    const paginationLabel = `${startRow} - ${endRow} of ${dataState.total}`;

    const rowMarkup = dataState.rows.map((transfer, index) => (
        <IndexTable.Row id={transfer.id} key={transfer.id} position={index}>
            <IndexTable.Cell>
                <Text variant="bodyMd" fontWeight="semibold" as="span">
                    {transfer.transferId}
                </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <CustomBadge status={transfer.status} />
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text>{transfer.type}</Text>
            </IndexTable.Cell>
            {isEmpty(outgoingLocation) && (
                <IndexTable.Cell>
                    <StoreLocation locationId={transfer.originLocation} />
                </IndexTable.Cell>
            )}
            {isEmpty(incomingLocation) && (
                <IndexTable.Cell>
                    <StoreLocation locationId={transfer.destinationLocation} />
                </IndexTable.Cell>
            )}
            <IndexTable.Cell>
                <Text as="span">{transfer.qtyShipped}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{transfer.qtyReceived}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{formatDateTime(transfer.createdAt)}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{formatDateTime(transfer.updatedAt)}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{formatDateTime(transfer.shippedDate)}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Button icon={ViewIcon} variant="plain" onClick={() => navigate(`/transfers/view/${transfer.id}`)} />
            </IndexTable.Cell>
        </IndexTable.Row>
    ));

    //set mode of filtering to filtering if filters are set, otherwise close filtering panel
    if (appliedFilters.length > 0 && mode == 'DEFAULT') {
        setMode('FILTERING');
    }

    return (
        <Layout.Section>
            <Card>
                <Box paddingBlock="400">
                    <InlineStack align="space-between" blockAlign="center" gap="200">
                        <Text variant="headingLg">{panelTitle}</Text>
                        <ExportButton type="TransferException" filters={currentFilters} />
                    </InlineStack>
                </Box>
                <div style={{ display: 'flex' }}>
                    <div style={{ flex: 1 }}>
                        <IndexFilters
                            key={JSON.stringify(appliedFilters)}
                            sortOptions={sortOptions}
                            sortSelected={sortSelected}
                            queryValue={queryValue}
                            queryPlaceholder="Search Transfers"
                            onQueryChange={handleQueryFilterChange}
                            onQueryClear={handleQueryValueRemove}
                            onSort={setSortSelected}
                            filters={filterOptions}
                            appliedFilters={appliedFilters}
                            onClearAll={handleFiltersClearAll}
                            mode={mode}
                            setMode={setMode}
                            tabs={tabs}
                            selected={0}
                            onSelect={() => {}}
                            canCreateNewView={false}
                            autoFocusSearchField={false}
                            cancelAction={{
                                onAction: handleSearchFieldCancelButtonClick,
                                disabled: false,
                                loading: false, // spinner handled manually
                            }}
                        />
                    </div>

                    {dataState.isLoading && (
                        <div style={{ padding: '12px' }}>
                            <Spinner accessibilityLabel="Loading transfers" size="small" />
                        </div>
                    )}
                </div>
                <IndexTable
                    condensed={breakpoints.smDown}
                    emptyState={emptyStateMarkup}
                    resourceName={resourceName}
                    itemCount={dataState.rows.length}
                    headings={tableHeadings}
                    selectable={false}
                    pagination={{
                        hasPrevious,
                        hasNext,
                        onNext: handleNextPage,
                        onPrevious: handlePreviousPage,
                        label: paginationLabel,
                    }}
                >
                    {rowMarkup}
                </IndexTable>
            </Card>
        </Layout.Section>
    );
}
